<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import type { DisclosureRequest } from '@/types/compliance'
import type { PaginatedResponse } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'DisclosureList' })

const { t } = useI18n()

const loading = ref(true)
const disclosures = ref<DisclosureRequest[]>([])
const statusFilter = ref<string>('all')
const searchQuery = ref('')

// Pagination
const pagination = usePagination({ initialItemsPerPage: 50 })

// Status filter options
const statusOptions = [
  { value: 'all', title: 'Alle' },
  { value: 'pending', title: 'Ausstehend' },
  { value: 'in_progress', title: 'In Bearbeitung' },
  { value: 'completed', title: 'Abgeschlossen' },
  { value: 'rejected', title: 'Abgelehnt' }
]

// Fetch disclosures
async function fetchDisclosures() {
  loading.value = true
  const { limit, offset } = pagination.buildQueryParams()

  try {
    const res = await useAxios<null, PaginatedResponse<DisclosureRequest>>(
      'GET',
      `/admin/disclosure?limit=${limit}&offset=${offset}`,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      disclosures.value = res.data.data
      pagination.setTotal(res.data.total)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'DisclosureList' })
  } finally {
    loading.value = false
  }
}

// Watch for page changes
watch(() => pagination.page.value, () => {
  fetchDisclosures()
})

// Pagination display helpers
const showingFrom = computed(() => ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1)
const showingTo = computed(() => Math.min(showingFrom.value + disclosures.value.length - 1, pagination.total.value))

// Filtered disclosures
const filteredDisclosures = computed(() => {
  let result = disclosures.value

  if (statusFilter.value !== 'all') {
    result = result.filter(d => d.status === statusFilter.value)
  }

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(d =>
      d.domain_name?.toLowerCase().includes(query) ||
      d.requester_name.toLowerCase().includes(query)
    )
  }

  return result
})

// Format date
function formatDate(dateString: string): string {
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

// Get deadline status
function getDeadlineStatus(deadline: string): { class: string; text: string } {
  const deadlineDate = DateTime.fromISO(deadline)
  const now = DateTime.now()
  const hoursRemaining = deadlineDate.diff(now, 'hours').hours

  if (hoursRemaining <= 0) {
    return { class: 'overdue', text: 'Überfällig' }
  } else if (hoursRemaining <= 24) {
    return { class: 'critical', text: `${Math.ceil(hoursRemaining)}h` }
  } else {
    const days = Math.ceil(hoursRemaining / 24)
    return { class: 'normal', text: `${days} Tage` }
  }
}

// Get status badge class
function getStatusClass(status: string): string {
  switch (status) {
    case 'pending': return 'status-pending'
    case 'in_progress': return 'status-progress'
    case 'completed': return 'status-completed'
    case 'rejected': return 'status-rejected'
    default: return ''
  }
}

// Get requester type label
function getRequesterTypeLabel(type: string): string {
  switch (type) {
    case 'law_enforcement': return 'Strafverfolgung'
    case 'cert': return 'CERT'
    case 'legal': return 'Rechtsanwalt'
    case 'trademark': return 'Markeninhaber'
    default: return type
  }
}

onMounted(() => {
  fetchDisclosures()
})
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.compliance.disclosure.title')"
    :subtitle="t('dashboard.admin.compliance.disclosure.subtitle')"
    icon="mdi-file-document-outline"
    icon-color="amber"
    show-back
    :back-route="{ name: 'dashboardAdminCompliance' }"
  >
    <template #actions>
      <v-btn
        color="primary"
        @click="$router.push({ name: 'dashboardAdminDisclosure', query: { create: 'true' } })"
      >
        <v-icon icon="mdi-plus" class="me-2" />
        {{ t('dashboard.admin.compliance.disclosure.create') }}
      </v-btn>
    </template>

    <!-- Filters -->
    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="t('dashboard.admin.compliance.disclosure.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
      <v-btn-toggle
        v-model="statusFilter"
        mandatory
        density="compact"
        class="status-toggle"
      >
        <v-btn
          v-for="opt in statusOptions"
          :key="opt.value"
          :value="opt.value"
          variant="outlined"
          size="small"
        >
          {{ opt.title }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredDisclosures.length === 0" class="empty-state">
      <v-icon icon="mdi-file-document-check-outline" size="64" />
      <h3>{{ t('dashboard.admin.compliance.disclosure.empty') }}</h3>
      <p>{{ t('dashboard.admin.compliance.disclosure.emptyDescription') }}</p>
    </div>

    <!-- Disclosure Table -->
    <div v-else class="table-container">
      <table class="disclosure-table">
        <thead>
          <tr>
            <th>{{ t('dashboard.admin.compliance.disclosure.columns.created') }}</th>
            <th>{{ t('dashboard.admin.compliance.disclosure.columns.domain') }}</th>
            <th>{{ t('dashboard.admin.compliance.disclosure.columns.requester') }}</th>
            <th>{{ t('dashboard.admin.compliance.disclosure.columns.deadline') }}</th>
            <th>{{ t('dashboard.admin.compliance.disclosure.columns.status') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="disclosure in filteredDisclosures"
            :key="disclosure.id"
            class="table-row"
          >
            <td class="date-cell">{{ formatDate(disclosure.created) }}</td>
            <td class="domain-cell">
              <span class="domain-name">{{ disclosure.domain_name }}</span>
            </td>
            <td class="requester-cell">
              <span class="requester-type">{{ getRequesterTypeLabel(disclosure.requester_type) }}</span>
              <span class="requester-name">{{ disclosure.requester_name }}</span>
            </td>
            <td class="deadline-cell">
              <span class="deadline-badge" :class="getDeadlineStatus(disclosure.deadline).class">
                {{ getDeadlineStatus(disclosure.deadline).text }}
              </span>
            </td>
            <td class="status-cell">
              <span class="status-badge" :class="getStatusClass(disclosure.status)">
                {{ disclosure.status }}
              </span>
            </td>
            <td class="action-cell">
              <v-btn
                icon
                variant="text"
                size="small"
              >
                <v-icon icon="mdi-chevron-right" />
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Controls -->
    <div v-if="pagination.total.value > 0 && !loading" class="pagination-controls">
      <div class="pagination-info">
        {{ t('dashboard.admin.compliance.disclosure.pagination.showing', { from: showingFrom, to: showingTo, total: pagination.total.value }) }}
      </div>
      <div class="pagination-buttons">
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === 1"
          @click="pagination.goToPage(1)"
        >
          <v-icon icon="mdi-chevron-double-left" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === 1"
          @click="pagination.prevPage()"
        >
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-indicator">
          {{ pagination.page.value }} / {{ pagination.totalPages.value }}
        </span>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === pagination.totalPages.value"
          @click="pagination.nextPage()"
        >
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === pagination.totalPages.value"
          @click="pagination.goToPage(pagination.totalPages.value)"
        >
          <v-icon icon="mdi-chevron-double-right" size="18" />
        </button>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Filters */
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-input {
  max-width: 300px;
  flex: 1;
}

.status-toggle {
  flex-wrap: wrap;
}

/* Loading/Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(200, 218, 255, 0.3);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0;
  }
}

/* Table */
.table-container {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.disclosure-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 16px;
    text-align: left;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: background 0.15s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }
}

.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  white-space: nowrap;
}

.domain-cell {
  .domain-name {
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }
}

.requester-cell {
  .requester-type {
    display: block;
    font-size: 0.75rem;
    color: rgba(200, 218, 255, 0.5);
    margin-bottom: 2px;
  }

  .requester-name {
    color: rgba(255, 255, 255, 0.8);
  }
}

.deadline-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;

  &.normal {
    background: rgba(76, 175, 80, 0.15);
    color: #81c784;
  }

  &.critical {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }

  &.overdue {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }
}

.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: capitalize;

  &.status-pending {
    background: rgba(255, 193, 7, 0.15);
    color: #ffc107;
  }

  &.status-progress {
    background: rgba(33, 150, 243, 0.15);
    color: #2196f3;
  }

  &.status-completed {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
  }

  &.status-rejected {
    background: rgba(158, 158, 158, 0.15);
    color: #9e9e9e;
  }
}

.action-cell {
  width: 48px;
  text-align: right;
}

/* Light Theme */
.v-theme--lightTheme {
  .table-container {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .disclosure-table th {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(15, 23, 42, 0.5);
  }

  .date-cell {
    color: rgba(15, 23, 42, 0.6);
  }

  .domain-cell .domain-name {
    color: rgba(15, 23, 42, 0.9);
  }

  .requester-cell .requester-name {
    color: rgba(15, 23, 42, 0.8);
  }

  .empty-state {
    h3 {
      color: rgba(15, 23, 42, 0.8);
    }

    p {
      color: rgba(15, 23, 42, 0.5);
    }
  }
}

/* Mobile */
@media (max-width: 768px) {
  .disclosure-list {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    max-width: 100%;
  }

  .table-container {
    overflow-x: auto;
  }

  .disclosure-table {
    min-width: 700px;
  }
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-top: 16px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px;
  min-width: 80px;
  text-align: center;
}
</style>
